import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useSquare } from './SquareProvider';
import { API_URL } from '../config/api';

const FormContainer = styled.form`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
`;

const PaymentMethodContainer = styled.div`
  border: 1px solid #ccc;
  padding: 15px;
  border-radius: 4px;
  background: white;
  margin-bottom: 20px;
`;

const PaymentMethodButton = styled.button`
  width: 100%;
  padding: 12px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #2c7d46;
  }

  &.selected {
    background: #e8f5e9;
    border-color: #2c7d46;
    font-weight: bold;
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`;

const CardContainer = styled.div`
  margin-top: 15px;
  min-height: 60px;
  display: ${props => props.$visible ? 'block' : 'none'};
`;

const Button = styled.button`
  background: #2c7d46;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;
  margin-top: 20px;

  &:hover {
    background: #36964f;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  margin-top: 10px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  color: #155724;
  background-color: #d4edda;
  border: 2px solid #2c7d46;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  line-height: 1.6;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const InfoMessage = styled.div`
  color: #1976d2;
  margin-top: 10px;
  font-size: 14px;
  background: #e3f2fd;
  padding: 10px;
  border-radius: 4px;
`;

const InputGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const OrderSummary = styled.div`
  background-color: #f9f9f9;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
`;

const SummaryTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 1.1rem;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 0.9rem;
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
`;

const SquareCheckoutForm = ({ amount, artworkTitle, artworkDetails, onSuccess, onCancel }) => {
  const { payments, isLoaded, error: squareError } = useSquare();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [card, setCard] = useState(null);
  const [applePay, setApplePay] = useState(null);
  const [googlePay, setGooglePay] = useState(null);
  const [giftCard, setGiftCard] = useState(null);
  const [ach, setACH] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState({
    message: '',
    isError: false,
    isSuccess: false
  });
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  // Use ref to prevent duplicate initialization
  const isInitialized = useRef(false);

  // Calculate amounts
  const salesTax = amount * 0.1;
  const shipping = 0;
  const total = amount + salesTax + shipping;

  useEffect(() => {
    const initializePaymentMethods = async () => {
      // Prevent duplicate initialization
      if (!payments || isInitialized.current) {
        console.log('⏸️ Skipping initialization (already initialized or payments not ready)');
        return;
      }

      console.log('🚀 Initializing payment methods (first time only)...');
      isInitialized.current = true;

      try {
        // Initialize and attach Card payment
        const cardInstance = await payments.card();
        // Wait for DOM
        setTimeout(async () => {
          try {
            await cardInstance.attach('#card-container');
            setCard(cardInstance);
            console.log('✅ Card payment method initialized and attached');
          } catch (e) {
            console.error('❌ Error attaching card:', e);
          }
        }, 200);

        // Initialize Apple Pay (if available)
        const paymentRequest = payments.paymentRequest({
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            amount: (total * 100).toString(), // Convert to cents
            label: `Joey M. Robinson Studio - ${artworkTitle}`
          }
        });

        try {
          const applePayInstance = await payments.applePay(paymentRequest);
          setApplePay(applePayInstance);
          console.log('✅ Apple Pay available');
        } catch (e) {
          console.log('ℹ️ Apple Pay not available:', e.message);
        }

        // Initialize Google Pay (if available)
        try {
          const googlePayInstance = await payments.googlePay(paymentRequest);
          setGooglePay(googlePayInstance);
          console.log('✅ Google Pay available');
        } catch (e) {
          console.log('ℹ️ Google Pay not available:', e.message);
        }

        // Initialize and attach Gift Card
        try {
          const giftCardInstance = await payments.giftCard();
          setTimeout(async () => {
            try {
              await giftCardInstance.attach('#giftcard-container');
              setGiftCard(giftCardInstance);
              console.log('✅ Gift Card payment method initialized and attached');
            } catch (e) {
              console.error('❌ Error attaching gift card:', e);
            }
          }, 200);
        } catch (e) {
          console.log('ℹ️ Gift Card not available:', e.message);
        }

        // Initialize and attach ACH
        try {
          const achInstance = await payments.ach();
          setTimeout(async () => {
            try {
              await achInstance.attach('#ach-container');
              setACH(achInstance);
              console.log('✅ ACH payment method initialized and attached');
            } catch (e) {
              console.error('❌ Error attaching ACH:', e);
            }
          }, 200);
        } catch (e) {
          console.log('ℹ️ ACH not available:', e.message);
        }
      } catch (e) {
        console.error('❌ Error initializing payment methods:', e);
        setPaymentStatus({
          message: 'Error initializing payment methods. Please refresh the page.',
          isError: true,
          isSuccess: false
        });
      }
    };

    if (isLoaded) {
      initializePaymentMethods();
    }

    return () => {
      // Cleanup payment method instances
      if (card) card.destroy();
      if (applePay) applePay.destroy();
      if (googlePay) googlePay.destroy();
      if (giftCard) giftCard.destroy();
      if (ach) ach.destroy();
    };
  }, [payments, isLoaded]);

  // No need for separate attachment effect - they're attached during initialization

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate customer information
    if (!customerInfo.name || !customerInfo.email) {
      setPaymentStatus({
        message: 'Please fill out all required fields.',
        isError: true,
        isSuccess: false
      });
      return;
    }

    setIsProcessing(true);
    setPaymentStatus({
      message: '',
      isError: false,
      isSuccess: false
    });

    try {
      let tokenResult;

      // Get payment token based on selected method
      if (selectedPaymentMethod === 'card' && card) {
        tokenResult = await card.tokenize();
      } else if (selectedPaymentMethod === 'applePay' && applePay) {
        tokenResult = await applePay.tokenize();
      } else if (selectedPaymentMethod === 'googlePay' && googlePay) {
        tokenResult = await googlePay.tokenize();
      } else if (selectedPaymentMethod === 'giftCard' && giftCard) {
        tokenResult = await giftCard.tokenize();
      } else if (selectedPaymentMethod === 'ach' && ach) {
        tokenResult = await ach.tokenize();
      }

      if (tokenResult.status === 'OK') {
        console.log('✅ Payment token generated:', tokenResult.token);
        console.log('📦 Frontend artworkDetails:', artworkDetails);
        console.log('📦 Frontend artworkTitle:', artworkTitle);
        console.log('🔄 Cache cleared - new version loaded');

        // REAL PAYMENT FLOW - Process payment through backend
        try {
          // Send the complete artwork object plus title
          const artworkPayload = {
            ...artworkDetails,
            title: artworkTitle,
            itemId: artworkDetails?.id,
            collectionId: artworkDetails?.collectionId
          };

          console.log('📦 FINAL VERSION - Sending artwork to backend:', artworkPayload);

          const apiUrl = process.env.REACT_APP_API_URL || `${API_URL}/api`;
          const response = await fetch(`${apiUrl}/process-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: tokenResult.token,
              amount: total,
              customer: customerInfo,
              artwork: artworkPayload
            })
          });

          const result = await response.json();

          if (response.ok && result.success) {
            console.log('✅ Payment processed successfully:', result.orderId);

            setPaymentStatus({
              message: `Thank you for your purchase of "${artworkTitle}"! Order ID: ${result.orderId}. We'll contact you shortly to arrange delivery.`,
              isError: false,
              isSuccess: true
            });
            setIsProcessing(false);

            // Wait 5 seconds before calling onSuccess to let user read the message
            setTimeout(() => {
              if (onSuccess) onSuccess();
            }, 5000);
          } else {
            // Payment failed on backend
            console.error('❌ Payment failed:', result.error);
            setPaymentStatus({
              message: result.error || 'Payment failed. Please try again.',
              isError: true,
              isSuccess: false
            });
            setIsProcessing(false);
          }
        } catch (error) {
          console.error('❌ Error processing payment:', error);
          setPaymentStatus({
            message: 'Network error. Please check your connection and try again.',
            isError: true,
            isSuccess: false
          });
          setIsProcessing(false);
        }
      } else {
        // Handle tokenization errors
        const errors = tokenResult.errors || [];
        const errorMessage = errors.length > 0
          ? errors.map(e => e.message).join(', ')
          : 'Failed to process payment. Please check your payment information.';

        setPaymentStatus({
          message: errorMessage,
          isError: true,
          isSuccess: false
        });
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('❌ Payment error:', error);
      setPaymentStatus({
        message: error.message || 'An error occurred while processing your payment.',
        isError: true,
        isSuccess: false
      });
      setIsProcessing(false);
    }
  };

  if (squareError) {
    return (
      <FormContainer>
        <ErrorMessage>Error loading payment form: {squareError}</ErrorMessage>
      </FormContainer>
    );
  }

  if (!isLoaded) {
    return (
      <FormContainer>
        <InfoMessage>Loading payment form...</InfoMessage>
      </FormContainer>
    );
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <h2>Complete Your Purchase</h2>

      <OrderSummary>
        <SummaryTitle>Order Summary</SummaryTitle>
        <SummaryItem>
          <span>{artworkTitle}</span>
          <span>${amount.toFixed(2)}</span>
        </SummaryItem>
        {artworkDetails && artworkDetails.medium && (
          <SummaryItem>
            <span>Medium: {artworkDetails.medium}</span>
          </SummaryItem>
        )}
        {artworkDetails && artworkDetails.size && (
          <SummaryItem>
            <span>Size: {artworkDetails.size}</span>
          </SummaryItem>
        )}
        <SummaryItem>
          <span>Sales Tax (10%)</span>
          <span>${salesTax.toFixed(2)}</span>
        </SummaryItem>
        <SummaryItem>
          <span>Shipping</span>
          <span>${shipping.toFixed(2)}</span>
        </SummaryItem>
        <SummaryTotal>
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </SummaryTotal>
      </OrderSummary>

      <h3>Contact Information</h3>
      <InputGroup>
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          name="name"
          type="text"
          value={customerInfo.name}
          onChange={handleInputChange}
          required
        />
      </InputGroup>

      <InputGroup>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={customerInfo.email}
          onChange={handleInputChange}
          required
        />
      </InputGroup>

      <h3>Shipping Information</h3>
      <InputGroup>
        <Label htmlFor="address">Address *</Label>
        <Input
          id="address"
          name="address"
          type="text"
          value={customerInfo.address}
          onChange={handleInputChange}
          required
        />
      </InputGroup>

      <InputGroup>
        <Label htmlFor="city">City *</Label>
        <Input
          id="city"
          name="city"
          type="text"
          value={customerInfo.city}
          onChange={handleInputChange}
          required
        />
      </InputGroup>

      <InputGroup>
        <Label htmlFor="state">State *</Label>
        <Input
          id="state"
          name="state"
          type="text"
          value={customerInfo.state}
          onChange={handleInputChange}
          required
        />
      </InputGroup>

      <InputGroup>
        <Label htmlFor="zip">ZIP Code *</Label>
        <Input
          id="zip"
          name="zip"
          type="text"
          value={customerInfo.zip}
          onChange={handleInputChange}
          required
        />
      </InputGroup>

      <h3>Payment Method</h3>
      <PaymentMethodContainer>
        <PaymentMethodButton
          type="button"
          className={selectedPaymentMethod === 'card' ? 'selected' : ''}
          onClick={() => setSelectedPaymentMethod('card')}
        >
          💳 Credit/Debit Card
        </PaymentMethodButton>

        {applePay && (
          <PaymentMethodButton
            type="button"
            className={selectedPaymentMethod === 'applePay' ? 'selected' : ''}
            onClick={() => setSelectedPaymentMethod('applePay')}
          >
            🍎 Apple Pay
          </PaymentMethodButton>
        )}

        {googlePay && (
          <PaymentMethodButton
            type="button"
            className={selectedPaymentMethod === 'googlePay' ? 'selected' : ''}
            onClick={() => setSelectedPaymentMethod('googlePay')}
          >
            🔵 Google Pay
          </PaymentMethodButton>
        )}

        {giftCard && (
          <PaymentMethodButton
            type="button"
            className={selectedPaymentMethod === 'giftCard' ? 'selected' : ''}
            onClick={() => setSelectedPaymentMethod('giftCard')}
          >
            🎁 Gift Card
          </PaymentMethodButton>
        )}

        {ach && (
          <PaymentMethodButton
            type="button"
            className={selectedPaymentMethod === 'ach' ? 'selected' : ''}
            onClick={() => setSelectedPaymentMethod('ach')}
          >
            🏦 Bank Transfer (ACH)
          </PaymentMethodButton>
        )}

        {/* All containers rendered, but only selected one is visible */}
        <CardContainer
          id="card-container"
          $visible={selectedPaymentMethod === 'card'}
        />
        <CardContainer
          id="giftcard-container"
          $visible={selectedPaymentMethod === 'giftCard'}
        />
        <CardContainer
          id="ach-container"
          $visible={selectedPaymentMethod === 'ach'}
        />
      </PaymentMethodContainer>

      {paymentStatus.isError && (
        <ErrorMessage>{paymentStatus.message}</ErrorMessage>
      )}

      {paymentStatus.isSuccess && (
        <SuccessMessage>
          ✅ {paymentStatus.message}
        </SuccessMessage>
      )}

      <InfoMessage>
        💡 {process.env.SQUARE_ENVIRONMENT === 'sandbox' || process.env.REACT_APP_SQUARE_ENVIRONMENT === 'sandbox'
          ? 'Testing mode: Use test card 4111 1111 1111 1111. No real charges will be made.'
          : 'Secure payment powered by Square. Your payment information is encrypted.'}
      </InfoMessage>

      <Button type="submit" disabled={isProcessing}>
        {isProcessing ? 'Processing...' : `Pay $${total.toFixed(2)}`}
      </Button>

      <Button
        type="button"
        onClick={onCancel}
        style={{
          background: 'transparent',
          color: '#666',
          marginTop: '10px',
          border: '1px solid #ccc'
        }}
      >
        Cancel
      </Button>
    </FormContainer>
  );
};

export default SquareCheckoutForm;
